/* Copyright 2016-2022
   Kabandy
   Author: Cedric Hoarau
 */

import { Common } from "../Utilities";
import { Network } from "./network";
import { ApiDataModel } from "../ApiDataModel";
import { ApiRouteFormatter, getApiKey, getApiVersion, isCloudRunEnabled } from "../ApiWeb";

type IApiVersion = ApiDataModel.IVersion;
type IApiVersionStatus = ApiDataModel.IVersionStatus;

let _rootDiv: HTMLDivElement | undefined = undefined;
let _currentVersionsHtmlListElement: HTMLUListElement | undefined = undefined;
let _currentSelectedVersionHtmlDivElement: HTMLDivElement | undefined = undefined;

let _accessToken: string = "";
let _onVersionSelected: Function | undefined = undefined;
let _currentVersions: IApiVersion[] = [];
let _currentVersionStatuses: IApiVersionStatus[] = [];

export module UIVersion {
    export function Bind(accessToken: string, workspaceId: string, modelgroupId: string, modelfileId: string, onVersionSelected: Function): void {
        _accessToken = accessToken;
        _onVersionSelected = onVersionSelected;

        _rootDiv = _InitializeUI(accessToken, workspaceId, modelgroupId, modelfileId);
        document.body.appendChild(_rootDiv);
    }
    export function Unbind() {
        if (_rootDiv) {
            _rootDiv.remove();
        }

        _currentVersions = [];
        _currentVersionStatuses = [];

        if (_currentVersionsHtmlListElement) {
            _currentVersionsHtmlListElement = undefined;
        }

        if (_currentSelectedVersionHtmlDivElement) {
            _currentSelectedVersionHtmlDivElement.style.fontWeight = "";
            _currentSelectedVersionHtmlDivElement.style.textDecoration = "";
            _currentSelectedVersionHtmlDivElement = undefined;
        }
    }

    function _InitializeUI(accessToken: string, workspaceId: string, modelgroupId: string, modelfileId: string): HTMLDivElement {
        let rootDiv = document.createElement("div");
        rootDiv.id = "ui-versions-root-div";

        let titleElement = document.createElement("h2");
        titleElement.innerText = "Versions";
        rootDiv.appendChild(titleElement);

        // get button
        let getButton = document.createElement("button");
        getButton.id = "ui-versions-get-button";
        getButton.textContent = "Get Versions";
        rootDiv.appendChild(getButton);
        // get click handler
        getButton.addEventListener("click", async () => {
            _currentVersions = await _FetchVersions(accessToken, workspaceId, modelgroupId, modelfileId);
            _currentVersionStatuses = await _FetchStatusesFromVersions(accessToken, _currentVersions);
            _currentVersionsHtmlListElement = _RefreshUIVersions(
                _currentVersionsHtmlListElement,
                modelgroupId,
                modelfileId,
                _currentVersionStatuses,
                _currentVersions
            );
        });

        // <br>
        let brElement = document.createElement("br");
        rootDiv.appendChild(brElement);

        // create input + label
        let createInput = document.createElement("input");
        createInput.id = "ui-versions-create-input";
        let createLabelElement = document.createElement("label");
        createLabelElement.textContent = "Uploaded file identifier";
        createLabelElement.setAttribute("for", createInput.id);
        rootDiv.appendChild(createLabelElement);
        rootDiv.appendChild(createInput);

        // create button
        let createButton = document.createElement("button");
        createButton.id = "ui-versions-create-button";
        createButton.textContent = "Create Version";
        rootDiv.appendChild(createButton);
        createButton.addEventListener("click", async () => {
            // @ts-ignore
            const sanitizedValue: string = DOMPurify.sanitize(createInput.value, { USE_PROFILES: { html: false } });
            await _CreateVersion(accessToken, workspaceId, modelgroupId, modelfileId, sanitizedValue);
            _currentVersions = await _FetchVersions(accessToken, workspaceId, modelgroupId, modelfileId);
            _currentVersionStatuses = await _FetchStatusesFromVersions(accessToken, _currentVersions);
            _currentVersionsHtmlListElement = _RefreshUIVersions(
                _currentVersionsHtmlListElement,
                modelgroupId,
                modelfileId,
                _currentVersionStatuses,
                _currentVersions
            );
        });

        // list of versions
        _currentVersionsHtmlListElement = _CreateUIVersions(modelgroupId, modelfileId, _currentVersionStatuses, _currentVersions);
        rootDiv.appendChild(_currentVersionsHtmlListElement);

        return rootDiv;
    }
    function _CreateUIVersions(
        modelgroupId: string,
        modelfileId: string,
        versionsStatuses: IApiVersionStatus[] = [],
        versions: IApiVersion[] = []
    ): HTMLUListElement {
        const oList = document.createElement("ul");

        for (let cVersion of versions) {
            let cListElement = document.createElement("li");
            cListElement.style.display = "flex";
            cListElement.style.alignItems = "center";

            let cVersionId: string = cVersion.id;
            let cFilename: string = cVersion.filename;
            let cFileSize: string = cVersion.fileSize;
            let cFileSizeDisplay: string = Common.fileSizeToString(cFileSize);
            let cVersionCreatedAt = cVersion.created_at;
            let cVersionUpdatedAt = cVersion.updated_at;

            let cStatusAsStr: string = "";
            let cStatus: IApiVersionStatus | undefined = versionsStatuses.find(status => cVersionId === status.versionId);
            if (cStatus) {
                cStatusAsStr = cStatus.value;
            }

            let cModelDiv: HTMLDivElement = document.createElement("div");
            cModelDiv.id = cVersionId;
            cModelDiv.textContent = `version ${cVersionId} | filename: ${cFilename} | filesize: ${cFileSizeDisplay} | created at: ${cVersionCreatedAt} | updated at: ${cVersionUpdatedAt} | status: ${cStatusAsStr}`;
            cListElement.appendChild(cModelDiv);
            cModelDiv.addEventListener("click", () => {
                // Remove bold + underline
                if (_currentSelectedVersionHtmlDivElement) {
                    _currentSelectedVersionHtmlDivElement.style.fontWeight = "";
                    _currentSelectedVersionHtmlDivElement.style.textDecoration = "";
                }

                // Add bold + underline
                cModelDiv.style.fontWeight = "bold";
                cModelDiv.style.textDecoration = "underline";

                // Swap selected modelgroup
                _currentSelectedVersionHtmlDivElement = cModelDiv;
            });

            // open button
            let cOpenButton : HTMLButtonElement = document.createElement( "button" );
            cOpenButton.id = "ui-versions-open-button-" + cVersionId;
            cOpenButton.textContent = "Open";
            cOpenButton.style.marginLeft = "10px";
            cListElement.appendChild(cOpenButton);
            cOpenButton.addEventListener("click", async () => {
                if (_onVersionSelected) {
                    _onVersionSelected(_accessToken, cVersion.workspaceId, cVersion.modelId, cVersionId);
                }
            });

            // get button
            let cGetButton: HTMLButtonElement = document.createElement("button");
            cGetButton.id = "ui-version-get-button-" + cVersionId;
            cGetButton.textContent = "Get";
            cGetButton.style.marginLeft = "10px";
            cListElement.appendChild(cGetButton);
            cGetButton.addEventListener("click", async () => {
                const [version, versionStatus]: [IApiVersion, IApiVersionStatus] = await Promise.all([
                    _FetchVersion(_accessToken, cVersion.workspaceId, modelgroupId, modelfileId, cVersionId),
                    _FetchStatusFromVersion(_accessToken, cVersion)
                ]);
                _UpdateUIVersionsFromVersion(modelgroupId, modelfileId, versionStatus, version);
            });

            // delete button
            let cDeleteButton: HTMLButtonElement = document.createElement("button");
            cDeleteButton.id = "ui-version-delete-button-" + cVersionId;
            cDeleteButton.textContent = "Delete";
            cDeleteButton.style.marginLeft = "10px";
            cListElement.appendChild(cDeleteButton);
            cDeleteButton.addEventListener("click", async () => {
                await _DeleteVersion(_accessToken, cVersion.workspaceId, modelgroupId, modelfileId, cVersionId);
                const versions: IApiVersion[] = await _FetchVersions(_accessToken, cVersion.workspaceId, modelgroupId, modelfileId);
                const versionsStatuses: IApiVersionStatus[] = await _FetchStatusesFromVersions(_accessToken, versions);
                _currentVersionsHtmlListElement = _RefreshUIVersions(
                    _currentVersionsHtmlListElement,
                    modelgroupId,
                    modelfileId,
                    versionsStatuses,
                    versions
                );
            });

            oList.appendChild(cListElement);
        }

        return oList;
    }
    function _RefreshUIVersions(
        currentList?: HTMLUListElement,
        modelgroupId?: string,
        modelfileId?: string,
        versionsStatuses: IApiVersionStatus[] = [],
        versions: IApiVersion[] = []
    ): HTMLUListElement | undefined {
        let oNextList: HTMLUListElement | undefined = undefined;

        if (currentList) {
            const nextList = _CreateUIVersions(modelgroupId, modelfileId, versionsStatuses, versions);

            const parentElement: HTMLElement | null = currentList.parentElement;
            if (parentElement) {
                parentElement.replaceChild(nextList, currentList);
                oNextList = nextList;
            }
        }

        return oNextList;
    }
    function _UpdateUIVersionsFromVersion(modelgroupId: string, modelfileId: string, versionStatus: IApiVersionStatus, version: IApiVersion): void {
        const updatedStatuses: IApiVersionStatus[] = _currentVersionStatuses.map(cVersionStatus =>
            versionStatus.id === cVersionStatus.id ? versionStatus : cVersionStatus
        );
        const updatedVersions: IApiVersion[] = _currentVersions.map(cVersion => (version.id === cVersion.id ? version : cVersion));
        _currentVersionsHtmlListElement = _RefreshUIVersions(
            _currentVersionsHtmlListElement,
            modelgroupId,
            modelfileId,
            updatedStatuses,
            updatedVersions
        );

        if (_currentSelectedVersionHtmlDivElement) {
            _currentSelectedVersionHtmlDivElement.style.fontWeight = "";
            _currentSelectedVersionHtmlDivElement.style.textDecoration = "";
            _currentSelectedVersionHtmlDivElement = undefined;
        }
    }

    //
    // --- Versions

    async function _FetchVersions(accessToken: string, workspaceId: string, modelId: string, modelfileId: string): Promise<IApiVersion[]> {
        const apiVersionsUrl: string = ApiRouteFormatter.FormatMeVersionsUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, modelId, modelfileId);
        const getMeVersionsResponse: { versions: IApiVersion[] } = await Network.GetMeVersions(apiVersionsUrl, getApiKey(), accessToken);
        return getMeVersionsResponse.versions || [];
    }
    async function _CreateVersion(
        accessToken: string,
        workspaceId: string,
        modelId: string,
        modelfileId: string,
        fileId: string
    ): Promise<IApiVersion> {
        const apiVersionsUrl: string = ApiRouteFormatter.FormatMeVersionsUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, modelId, modelfileId);
        const createMeVersionResponse: { version: IApiVersion } = await Network.PostMeVersions(apiVersionsUrl, getApiKey(), accessToken, fileId);
        return createMeVersionResponse.version;
    }
    async function _FetchVersion(
        accessToken: string,
        workspaceId: string,
        modelId: string,
        modelfileId: string,
        versionId: string
    ): Promise<IApiVersion> {
        const apiVersionUrl: string = ApiRouteFormatter.FormatMeVersionUrl(
            isCloudRunEnabled(),
            getApiVersion(),
            workspaceId,
            modelId,
            modelfileId,
            versionId
        );
        const getMeVersionResponse: { version: IApiVersion } = await Network.GetMeVersion(apiVersionUrl, getApiKey(), accessToken);
        return getMeVersionResponse.version;
    }
    async function _DeleteVersion(accessToken: string, workspaceId: string, modelId: string, modelfileId: string, versionId: string): Promise<void> {
        const apiVersionUrl: string = ApiRouteFormatter.FormatMeVersionUrl(
            isCloudRunEnabled(),
            getApiVersion(),
            workspaceId,
            modelId,
            modelfileId,
            versionId
        );
        await Network.DeleteMeVersion(apiVersionUrl, getApiKey(), accessToken);
    }

    //
    // --- Statuses

    async function _FetchStatusesFromVersions(accessToken: string, versions: IApiVersion[] = []): Promise<IApiVersionStatus[]> {
        return Promise.all(
            versions.reduce(
                (statuses: Promise<IApiVersionStatus>[], version: IApiVersion) =>
                    version ? [...statuses, _FetchStatusFromVersion(accessToken, version)] : statuses,
                []
            )
        );
    }
    async function _FetchStatusFromVersion(accessToken: string, version: IApiVersion): Promise<IApiVersionStatus> {
        const apiVersionStatusUrl: string = ApiRouteFormatter.FormatMeVersionStatusUrl(
            isCloudRunEnabled(),
            getApiVersion(),
            version.workspaceId,
            version.modelId,
            version.modelfileId,
            version.id
        );
        const getMeVersionStatusResponse: { status: IApiVersionStatus } = await Network.GetMeVersionStatus(
            apiVersionStatusUrl,
            getApiKey(),
            accessToken
        );
        return getMeVersionStatusResponse.status;
    }
}
